/**
 * API 客户端配置
 */
import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { clearAuth } from '@/lib/auth-manager';
import { ROUTES } from '@/constants/routes';
import { toast } from '@/hooks/use-toast';
import { errorTracker } from '@/lib/error-tracking';
import { analyticsTracker } from '@/lib/analytics';
import { queryClient } from '@/app/providers/query-client';
import {
  getProjectIdsFromLocation,
  isParseFlowRecoverableSubprojectMiss,
  matchesProjectAccessRevocation,
} from '@/lib/access-revocation';
import { getStoredReadonlyProjectShareToken } from '@/stores/shareAccessStore';
import { useAccessRevocationStore } from '@/stores/accessRevocationStore';
import { normalizeApiErrorMessage } from '@/lib/error-utils';
import { resolveRequestOrgId } from '@/lib/route-context-scope';
import { isPublicSharedRequestUrl } from '@/lib/public-share-access';
import { updateServerClockSkew } from '@/lib/server-clock';
import { applyCoordinationResponse } from './screenplay-coordination';

// 向后兼容 re-export，外部模块应优先从 @/lib/error-utils 导入
export { normalizeApiErrorMessage };

let isRedirectingToWorkspace = false;
let isHandlingTempMemberAccessLoss = false;
let isReloadingForMaintenance = false;

export function isUnauthenticatedApiError(
  statusCode: number | undefined,
  message: unknown
): boolean {
  if (statusCode === 401) return true;
  if (statusCode !== 403 || typeof message !== 'string') return false;
  return message.trim().toLowerCase() === 'not authenticated';
}

const redirectToWorkspace = () => {
  if (window.location.pathname === ROUTES.WORKSPACE || isRedirectingToWorkspace) return;
  isRedirectingToWorkspace = true;
  window.location.replace(ROUTES.WORKSPACE);
};

function isUnifiedApiErrorPayload(data: unknown): boolean {
  return Boolean(
    data && typeof data === 'object' && typeof (data as { success?: unknown }).success === 'boolean'
  );
}

function reloadOnServiceUnavailable(statusCode: number | undefined, data: unknown) {
  if (statusCode !== 503 || isReloadingForMaintenance || typeof window === 'undefined') {
    return;
  }
  if (isUnifiedApiErrorPayload(data)) {
    return;
  }

  isReloadingForMaintenance = true;
  window.location.reload();
}

const PROJECT_RESOURCE_RE = /^\/projects\/([^/]+)(?:\/.*)?$/;
const AI_CONVERSATION_RESOURCE_RE = /^\/ai\/conversations\/[^/]+(?:\/.*)?$/;

function normalizeApiRequestPath(url?: string): string {
  if (!url) return '';
  let pathname = url;
  try {
    pathname = new URL(url, window.location.origin).pathname;
  } catch {
    pathname = url.split('?')[0] ?? url;
  }
  return pathname.replace(/^\/api\/v\d+/, '');
}

function isAuthEntryRequest(url?: string): boolean {
  const path = normalizeApiRequestPath(url);
  return (
    path === '/auth/login/phone' ||
    path === '/auth/login/password' ||
    path === '/auth/login/email' ||
    path === '/auth/register' ||
    path === '/auth/password/reset/send-sms' ||
    path === '/auth/password/reset/verify'
  );
}

function isParseFlowPath(pathname: string): boolean {
  return (
    pathname === ROUTES.PARSE_FLOW ||
    pathname.startsWith(`${ROUTES.PARSE_FLOW}/`) ||
    /^\/project\/[^/]+\/parse-flow(?:\/|$)/.test(pathname)
  );
}

/** 仅读请求失败表示「当前页资源不可访问」；写操作 403 由 toast 提示即可 */
function isProjectAccessReadRequest(method?: string): boolean {
  const normalized = (method ?? 'get').toLowerCase();
  return normalized === 'get' || normalized === 'head';
}

export function shouldShowProjectAccessRevocationForApiError({
  statusCode,
  requestUrl,
  pathname,
  search,
  method,
}: {
  statusCode: number | undefined;
  requestUrl: string;
  pathname: string;
  search: string;
  method?: string;
}): boolean {
  if (statusCode !== 403 && statusCode !== 404) return false;
  if (!isParseFlowPath(pathname)) return false;
  if (!isProjectAccessReadRequest(method)) return false;

  const requestPath = normalizeApiRequestPath(requestUrl);
  const projectId = requestPath.match(/^\/projects\/([^/]+)\/?$/)?.[1];
  if (!projectId && AI_CONVERSATION_RESOURCE_RE.test(requestPath)) {
    return getProjectIdsFromLocation(pathname, search).size > 0;
  }
  if (!projectId) return false;
  if (
    isParseFlowRecoverableSubprojectMiss({
      statusCode,
      pathname,
      search,
      projectId,
    })
  ) {
    return false;
  }
  return matchesProjectAccessRevocation({
    pathname,
    search,
    projectId,
  });
}

function showProjectAccessRevocationForApiError(
  statusCode: number | undefined,
  requestUrl: string,
  method?: string
) {
  if (typeof window === 'undefined') return false;
  if (
    !shouldShowProjectAccessRevocationForApiError({
      statusCode,
      requestUrl,
      pathname: window.location.pathname,
      search: window.location.search,
      method,
    })
  ) {
    return false;
  }

  const projectId =
    normalizeApiRequestPath(requestUrl).match(PROJECT_RESOURCE_RE)?.[1] ??
    Array.from(getProjectIdsFromLocation(window.location.pathname, window.location.search))[0];
  useAccessRevocationStore.getState().show({
    kind: 'project',
    projectId,
    affectedProjectIds: projectId ? [projectId] : [],
    description:
      statusCode === 404
        ? '项目不存在或已被删除，当前页面将自动返回工作台。'
        : '你已不在该项目或组织中，当前页面将自动返回工作台。',
  });
  return true;
}

const shouldRedirectOn404 = (
  url?: string,
  method?: string,
  pathname = window.location.pathname,
  search = window.location.search
) => {
  if (!url) return false;
  if (!isProjectAccessReadRequest(method)) return false;
  // 仅项目主资源缺失时跳转 workspace，避免子资源 404 误触发整页跳转
  const requestPath = normalizeApiRequestPath(url);
  const projectId = requestPath.match(/^\/projects\/([^/]+)\/?$/)?.[1];
  if (!projectId) return false;
  if (
    isParseFlowRecoverableSubprojectMiss({
      statusCode: 404,
      pathname,
      search,
      projectId,
    })
  ) {
    return false;
  }
  return getProjectIdsFromLocation(pathname, search).has(projectId);
};

const shouldReportOn403 = (url?: string) => {
  if (!url) return false;
  // 项目主资源 403（越权访问）具有安全审计价值，需要上报
  return /^\/projects\/[^/]+\/?$/.test(normalizeApiRequestPath(url));
};

function getBusinessErrorCode(data: unknown): string | undefined {
  if (!data || typeof data !== 'object') return undefined;
  const obj = data as Record<string, unknown>;
  const nested =
    obj.data && typeof obj.data === 'object' ? (obj.data as Record<string, unknown>) : {};
  const value =
    obj.errorCode ?? obj.error_code ?? nested.errorCode ?? nested.error_code ?? nested.code;
  return typeof value === 'string' ? value : undefined;
}

/**
 * 是否为「URL / 调用方显式」指定的激活组织头。
 * axios 拦截器若无条件用 session 覆盖，会破坏首屏与路由切换时与 URL 对齐的请求（例如 Workspace 已传 orgId 仍会按旧 session 拉回个人工作区树）。
 */
function hasExplicitActiveOrgHeader(headers: InternalAxiosRequestConfig['headers']): boolean {
  if (!headers || typeof headers !== 'object') return false;
  const withHas = headers as { has?: (name: string) => boolean };
  if (typeof withHas.has === 'function') {
    return (
      withHas.has('X-Active-Org-Id') ||
      withHas.has('x-active-org-id') ||
      withHas.has('X-active-org-id')
    );
  }
  const withGet = headers as { get?: (name: string) => unknown };
  if (typeof withGet.get === 'function') {
    return (
      withGet.get('X-Active-Org-Id') !== undefined ||
      withGet.get('x-active-org-id') !== undefined ||
      withGet.get('X-active-org-id') !== undefined
    );
  }
  const plain = headers as Record<string, unknown>;
  return (
    Object.prototype.hasOwnProperty.call(plain, 'X-Active-Org-Id') ||
    Object.prototype.hasOwnProperty.call(plain, 'x-active-org-id')
  );
}

function handleTempMemberAccessLoss(errorCode: string | undefined) {
  if (errorCode !== 'TEMP_MEMBER_REVOKED' && errorCode !== 'TEMP_MEMBER_EXPIRED') return false;
  if (isHandlingTempMemberAccessLoss) return true;
  isHandlingTempMemberAccessLoss = true;
  queryClient.invalidateQueries({ queryKey: ['projects'] });
  queryClient.invalidateQueries({ queryKey: ['project-shares'] });
  queryClient.invalidateQueries({ queryKey: ['temp-members'] });
  toast({
    title: '您已离开该项目',
    description:
      errorCode === 'TEMP_MEMBER_EXPIRED' ? '临时成员身份已到期。' : '临时成员访问已被移除。',
    variant: 'destructive',
  });
  window.setTimeout(() => {
    window.location.replace(ROUTES.WORKSPACE);
  }, 800);
  return true;
}

// 创建 axios 实例
const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 检查认证是否已失效（401/403 后阻止所有后续请求）
    const isClearing = sessionStorage.getItem('auth_clearing');
    if (isClearing === 'true') {
      console.log('🚫 认证已失效，拒绝请求:', config.url);
      return Promise.reject(new Error('认证失效，请重新登录'));
    }

    // FormData 需由浏览器自动设置 Content-Type（含 boundary），否则后端无法解析 multipart
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type'];
    }

    // 添加 token
    const token = localStorage.getItem('token');

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // URL 是空间路由（含工具箱）的权威上下文；项目页等未知路由才回退 session。
    if (config.headers && !hasExplicitActiveOrgHeader(config.headers)) {
      const orgId = resolveRequestOrgId(
        typeof window === 'undefined' ? '' : window.location.pathname,
        sessionStorage.getItem('active_org_id')
      );
      if (orgId) config.headers['X-Active-Org-Id'] = orgId;
    }

    const readonlyProjectShareToken = getStoredReadonlyProjectShareToken();
    if (readonlyProjectShareToken && config.headers) {
      config.headers['X-Project-Share-Token'] = readonlyProjectShareToken;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// 响应拦截器
apiClient.interceptors.response.use(
  (response) => {
    const res = response.data;

    // 每次响应都用服务端 timestamp 纠正本机时钟偏移，供撤回倒计时等场景使用
    if (res && typeof res === 'object' && 'timestamp' in res) {
      updateServerClockSkew((res as { timestamp?: unknown }).timestamp);
    }

    // 检查是否为统一响应格式 (RFC 135)
    // 新格式：{ success, code, message, data, timestamp }
    if (res && typeof res === 'object' && typeof res.success === 'boolean') {
      // 成功响应
      if (res.success) {
        if (res.coordination?.identity && res.coordination?.state) {
          applyCoordinationResponse(queryClient, res.coordination);
        }
        return res.data;
      }

      // 业务错误响应 (success = false)
      const message = normalizeApiErrorMessage(res.message || 'Unknown Error');
      console.error(`❌ API 业务错误 [${res.code}]:`, message);

      const error = new Error(message);
      (error as any).response = response;
      (error as any).code = res.code;
      (error as any).data = res.data;
      return Promise.reject(error);
    }

    // 兼容旧格式：{ code, message, data }（无 success 字段）
    if (res && typeof res === 'object' && typeof res.code === 'number') {
      if (res.code === 200) {
        return res.data;
      }

      const message = normalizeApiErrorMessage(res.message || 'Unknown Error');
      console.error(`❌ API 业务错误 [${res.code}]:`, message);

      const error = new Error(message);
      (error as any).response = response;
      (error as any).code = res.code;
      return Promise.reject(error);
    }

    // 非统一格式（可能是旧接口、外部接口或 Blob），直接返回
    return res;
  },
  (error: AxiosError) => {
    // 主动取消属于调用方的正常生命周期控制，不应记录成网络故障或上报监控。
    if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
      return Promise.reject(error);
    }

    const errorData = error.response?.data as unknown;
    console.error('❌ API 错误:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: error.config?.url,
      method: error.config?.method,
      requestData: error.config?.data,
    });

    const method = error.config?.method;
    const requestUrl = error.config?.url ?? '';
    reloadOnServiceUnavailable(error.response?.status, error.response?.data);

    // 处理统一错误响应格式
    if (errorData && typeof errorData === 'object' && 'timestamp' in errorData) {
      updateServerClockSkew((errorData as { timestamp?: unknown }).timestamp);
    }
    if (
      errorData &&
      typeof errorData === 'object' &&
      typeof (errorData as { success?: unknown }).success === 'boolean' &&
      !(errorData as { success: boolean }).success
    ) {
      const unified = errorData as {
        code?: number;
        message?: string;
        errorCode?: string;
        data?: unknown;
        timestamp?: string;
      };
      const message = normalizeApiErrorMessage(unified.message || 'Unknown Error');
      console.error(`❌ API 错误 [${unified.code}]:`, message);

      const customError = new Error(message);
      (customError as any).code = unified.code;
      (customError as any).errorCode = unified.errorCode;
      (customError as any).data = unified.data;
      (customError as any).timestamp = unified.timestamp;

      // 认证失效：后端无 token 场景可能返回 403 Not authenticated
      if (
        isUnauthenticatedApiError(unified.code, message) &&
        !isPublicSharedRequestUrl(requestUrl) &&
        !isAuthEntryRequest(requestUrl)
      ) {
        console.error(`🔒 认证失效 (${unified.code}):`, message);
        clearAuth(`API 错误 ${unified.code}: ${message}`);
      }

      // 402 词元不足
      if (unified.code === 402) {
        console.warn('⚠️ 词元不足 (402):', message);
        analyticsTracker.track('ai_credits_insufficient');
      }

      // 403 权限不足：不清除 token，保持登录状态
      if (unified.code === 403 && !isUnauthenticatedApiError(unified.code, message)) {
        console.warn('⚠️ 权限不足 (403):', message);
        handleTempMemberAccessLoss(getBusinessErrorCode(unified));
        showProjectAccessRevocationForApiError(unified.code, requestUrl, method);
        // 项目主资源 403 上报（越权访问具有安全审计价值）
        if (shouldReportOn403(requestUrl)) {
          errorTracker.captureApiError(customError, {
            method: method ?? 'unknown',
            url: requestUrl,
            statusCode: unified.code,
          });
        }
      }

      // 404 资源不存在：仅项目主资源缺失时跳转到 workspace
      if (
        unified.code === 404 &&
        shouldRedirectOn404(requestUrl, method, window.location.pathname, window.location.search)
      ) {
        console.warn('⚠️ 资源不存在 (404):', message);
        if (!showProjectAccessRevocationForApiError(unified.code, requestUrl, method)) {
          redirectToWorkspace();
        }
      }

      // 5xx 及关键业务错误上报（过滤 401/403/404 降噪）
      const reportableCode = unified.code;
      if (reportableCode && reportableCode >= 500) {
        errorTracker.captureApiError(customError, {
          method: method ?? 'unknown',
          url: requestUrl,
          statusCode: reportableCode,
        });
      }

      return Promise.reject(customError);
    }

    // 兼容旧格式错误处理
    const legacyDetail = (error.response?.data as any)?.detail;
    if (
      isUnauthenticatedApiError(error.response?.status, legacyDetail) &&
      !isPublicSharedRequestUrl(requestUrl) &&
      !isAuthEntryRequest(requestUrl)
    ) {
      console.error(`🔒 认证失效 (${error.response?.status}):`, legacyDetail);
      clearAuth(`HTTP ${error.response?.status}: ${legacyDetail || '未授权'}`);
    }

    if (
      error.response?.status === 403 &&
      !isUnauthenticatedApiError(error.response?.status, legacyDetail)
    ) {
      const detail = legacyDetail;
      console.warn('⚠️ 权限不足 (403):', detail);
      handleTempMemberAccessLoss(getBusinessErrorCode(error.response?.data));
      if (shouldReportOn403(requestUrl)) {
        errorTracker.captureApiError(error, {
          method: method ?? 'unknown',
          url: requestUrl,
          statusCode: 403,
        });
      }
    }

    if (
      error.response?.status === 404 &&
      shouldRedirectOn404(requestUrl, method, window.location.pathname, window.location.search)
    ) {
      const detail = (error.response?.data as any)?.detail;
      console.warn('⚠️ 资源不存在 (404):', detail);
      redirectToWorkspace();
    }

    // 5xx 及网络错误上报
    const httpStatus = error.response?.status;
    if (!httpStatus || httpStatus >= 500) {
      errorTracker.captureApiError(error, {
        method: method ?? 'unknown',
        url: requestUrl,
        statusCode: httpStatus,
      });
    }

    return Promise.reject(error);
  }
);

export default apiClient;
